import { useState, useEffect } from "react";
import { useParams } from "wouter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, CheckCircle, AlertTriangle, Clock, Award } from "lucide-react";

interface SharedDossier {
  summary: string;
  fitScore: number;
  highlights: string[];
  concerns: string[];
  recommendation: string;
  verifiedAt: string;
}

interface ShareData {
  dossier: SharedDossier;
  expiresAt: string;
  accessCount: number;
}

export default function SharedDossierPage() {
  const params = useParams<{ token: string }>();
  const [data, setData] = useState<ShareData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDossier();
  }, [params.token]);

  const fetchDossier = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/shared/dossier/${params.token}`);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to load dossier");
      }
      
      const shareData = await res.json();
      setData(shareData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Loading candidate profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground">{error}</p>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { dossier } = data;
  const scoreColor = dossier.fitScore >= 80 ? "text-green-500" : dossier.fitScore >= 60 ? "text-yellow-500" : "text-red-500";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            <span className="font-bold text-xl">VEXIS</span>
            <Badge variant="outline" className="ml-2">Agent-Verified</Badge>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>Access {data.accessCount}</span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card className="p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">Candidate Profile</h1>
              <p className="text-muted-foreground">
                Agent-verified candidate summary
              </p>
            </div>
            <div className="text-right">
              <div className={`text-4xl font-bold ${scoreColor}`}>
                {dossier.fitScore}%
              </div>
              <p className="text-sm text-muted-foreground">Fit Score</p>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <Badge variant="default" className="gap-1 bg-green-600">
              <CheckCircle className="w-3 h-3" />
              Identity Verified
            </Badge>
            <Badge variant="default" className="gap-1 bg-green-600">
              <CheckCircle className="w-3 h-3" />
              Right to Work Confirmed
            </Badge>
            <Badge variant="default" className="gap-1 bg-green-600">
              <CheckCircle className="w-3 h-3" />
              Skills Assessed
            </Badge>
          </div>

          <div className="prose max-w-none">
            <h2 className="text-xl font-semibold mb-3">Summary</h2>
            <p className="text-slate-700 leading-relaxed">
              {dossier.summary || "No summary available."}
            </p>
          </div>
        </Card>

        {dossier.highlights && dossier.highlights.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-green-500" />
              Key Strengths
            </h2>
            <ul className="space-y-2">
              {dossier.highlights.map((highlight, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {dossier.concerns && dossier.concerns.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Considerations
            </h2>
            <ul className="space-y-2">
              {dossier.concerns.map((concern, i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <span>{concern}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {dossier.recommendation && (
          <Card className="p-6 bg-primary/5 border-primary/20">
            <h2 className="text-xl font-semibold mb-3">Agent Recommendation</h2>
            <p className="text-slate-700">{dossier.recommendation}</p>
          </Card>
        )}

        <div className="text-center mt-8 text-sm text-muted-foreground">
          <p>
            Verified by VEXIS Agent on {dossier.verifiedAt ? new Date(dossier.verifiedAt).toLocaleDateString() : "N/A"}
          </p>
          <p className="mt-1">
            Contact details protected. Please contact your recruiting agent for candidate information.
          </p>
        </div>
      </div>
    </div>
  );
}
